import api from "../utils/api";
import qs from "../utils/qs";

// In-flight dedup: if the same request is already in flight, reuse the same Promise
const _inFlight = new Map();
const dedupRequest = (key, fn) => {
  if (_inFlight.has(key)) return _inFlight.get(key);
  const promise = fn().finally(() => _inFlight.delete(key));
  _inFlight.set(key, promise);
  return promise;
};

// Session-level cache for accounts list — invalidated on account create/delete/switch
let _accountListCache = null;
export const invalidateAccountListCache = () => { _accountListCache = null; };
export const setAccountListCache = (data) => { _accountListCache = data; };

export const CreateAccount = async (data) => {
    try {
        const response = await api.post(`/accounts`, data);
        if (response.status === 200 || response.status === 201) {
            return response.data;
        }
        return { error: "Unexpected response while creating account." };
    } catch (error) {
        return { error: error?.response?.data?.message || error?.message };
    }
}

export const FetchAccount = async (params) => {
    try {
        const response = await api.get(`/accounts?${qs(params)}`);
        if (response?.status === 200) {
            return response?.data;
        }
    } catch (error) {
        return { error: error?.response?.data?.message || error?.message };
    }
}

export const FetchAccountID = (id) =>
  dedupRequest(`account:${id}`, async () => {
    try {
      const response = await api.get(`/accounts/${id}`);
      if (response.status === 200) return response.data;
    } catch (error) {
      console.error("Error fetching account data:", error);
      return { error: error?.response?.data?.message || error?.message };
    }
  });


export const EditAccount = async (id, data) => {
    try {
        const response = await api.put(`/accounts/${id}`, data); // Include ID in the endpoint URL
        if (response.status === 200) {
            return response?.data;
        }
    } catch (error) {
        return { error: error?.response?.data?.message || error?.message };
    }
};

export const validatetoken = async (data) => {
    try {
        const response = await api.post(`/whatsapp/validate-token`, data); // Include ID in the endpoint URL
        if (response.status === 200) {
            return response?.data;
        }
    } catch (error) {
        return { error: error?.response?.data?.message || error?.message };
    }
};

export const addCredentials = async (data) => {
    try {
        const response = await api.post(`/whatsapp/whatsapp-credentials`, data); // Include ID in the endpoint URL
        return response
    } catch (error) {
        return { error: error?.response?.data?.message || error?.message };
    }
};

export const updateCredentials = async (id, data) => {
    try {
        const response = await api.put(`/whatsapp/whatsapp-credentials/${id}`, data);
        return response;
    } catch (error) {
        return { error: error?.response?.data?.message || error?.response?.data?.error || error?.message };
    }
};


export const DeleteAccount = async (id) => {
    try {
        const response = await api.delete(`/accounts/${id}`); // Include ID in the endpoint URL
        if (response.status === 200) {
            return response.data;
        }
    } catch (error) {
        return { error: error.response?.data?.message || error.message };
    }
};

export const FetchAccountList = () =>
  dedupRequest("accounts:list", async () => {
    // Return session cache if populated — avoids duplicate fetches on navigation/re-renders
    if (_accountListCache && !_accountListCache.error) return _accountListCache;
    try {
      const response = await api.get(`/accounts`);
      if (response?.status === 200) {
        _accountListCache = response?.data;
        return _accountListCache;
      }
    } catch (error) {
      return { error: error?.response?.data?.message || error?.message };
    }
  });

export const getSwitchAccount = async (id) => {
    try {
        const response = await api.get(`/accounts/${id}/switch`); // Include the ID in the endpoint
        if (response.status === 200) {
            return response.data; // Return the fetched data
        }
    } catch (error) {
        console.error("Error fetching account data:", error);
        return { error: error?.response?.data?.message || error?.message };
    }
};

export const rechargeCredits = async (payload) => {
  try {
    const response = await api.post("/payments/credit-recharge", payload);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const FetchAccountTheme = async (id) => {
  try {
    const response = await api.get(`/accounts/${id}/theme`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const UpdateAccountTheme = async (id, brandingTheme) => {
  try {
    const response = await api.put(`/accounts/${id}/theme`, {
      branding_theme: brandingTheme,
    });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const ImportBrandTheme = async (id, payload) => {
  try {
    const response = await api.post(`/accounts/${id}/theme/import-brand`, payload);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const UploadBrandingLogo = async (id, file, slot) => {
  try {
    const formData = new FormData();
    formData.append("logo", file);
    formData.append("slot", slot);
    const response = await api.post(`/accounts/${id}/theme/upload-branding-logo`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const FetchShopifyConfig = async (accountId) => {
  try {
    const response = await api.get(`/accounts/${accountId}/shopify/config`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const UpdateShopifyConfig = async (accountId, payload) => {
  try {
    const response = await api.put(`/accounts/${accountId}/shopify/config`, payload);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const ValidateShopifyStoreDomain = async (accountId, payload) => {
  try {
    const response = await api.post(`/accounts/${accountId}/shopify/store-domain/validate`, payload);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return {
      error: error?.response?.data?.message || error?.message,
      code: error?.response?.data?.code
    };
  }
};

export const TestShopifyConnection = async (accountId, payload = {}) => {
  try {
    const response = await api.post(`/accounts/${accountId}/shopify/test-connection`, payload);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return {
      error: error?.response?.data?.message || error?.message,
      code: error?.response?.data?.code
    };
  }
};

export const UpdateShopifyStoreDomain = async (accountId, payload) => {
  try {
    const response = await api.patch(`/accounts/${accountId}/shopify/store-domain`, payload);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return {
      error: error?.response?.data?.message || error?.message,
      code: error?.response?.data?.code
    };
  }
};

export const CreateShopifyConnectUrl = async (accountId, payload = {}) => {
  try {
    const response = await api.post(`/accounts/${accountId}/shopify/connect-url`, payload);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const DisconnectShopify = async (accountId) => {
  try {
    const response = await api.post(`/accounts/${accountId}/shopify/disconnect`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const FetchAccountClientApps = async (accountId) => {
  try {
    const response = await api.get(`/accounts/${accountId}/client-apps`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const FetchAccountApiCredentials = async (accountId) => {
  try {
    const response = await api.get(`/accounts/${accountId}/client-apps/credentials`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const SaveAccountApiCredentials = async (accountId, payload) => {
  try {
    const response = await api.put(`/accounts/${accountId}/client-apps/credentials`, payload);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const GenerateAccountApiCredentials = async (accountId, payload = {}) => {
  try {
    const response = await api.post(`/accounts/${accountId}/client-apps/credentials/generate`, payload);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const CreateAccountClientApp = async (accountId, payload) => {
  try {
    const response = await api.post(`/accounts/${accountId}/client-apps`, payload);
    if (response.status === 201) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const RotateAccountClientAppSecret = async (accountId, clientAppId) => {
  try {
    const response = await api.post(`/accounts/${accountId}/client-apps/${clientAppId}/rotate-secret`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const DeactivateAccountClientApp = async (accountId, clientAppId) => {
  try {
    const response = await api.patch(`/accounts/${accountId}/client-apps/${clientAppId}/deactivate`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const FetchAccountFeatures = async (accountId) => {
  try {
    const response = await api.get(`/accounts/${accountId}/features`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const UpdateAccountFeatures = async (accountId, features = []) => {
  try {
    const response = await api.put(`/accounts/${accountId}/features`, { features });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const PurgeAccountCache = async (accountId) => {
  try {
    const response = await api.post(`/accounts/${accountId}/cache/purge`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

const normalizeAgenticError = (error) => {
  const responseData = error?.response?.data || {};
  const code = String(responseData?.code || "").toUpperCase();
  const readinessPayload = responseData?.overall === "ready" || responseData?.overall === "degraded";
  const unavailable = !readinessPayload && (
    responseData?.unavailable === true
    || code === "AGENTIC_SERVICE_UNAVAILABLE"
    || [502, 503, 504].includes(Number(error?.response?.status || 0))
  );
  return {
    error: responseData?.message || error?.message,
    code: code || undefined,
    unavailable,
    retryAfterSeconds: responseData?.retryAfterSeconds,
    details: responseData,
  };
};

const buildAgenticRequestConfig = (config = {}) => ({
  suppressErrorToast: true,
  ...config,
});

export const TriggerAccountRagSync = async (accountId, payload = {}) => {
  try {
    const response = await api.post(`/accounts/${accountId}/agentic/rag-sync`, payload, buildAgenticRequestConfig());
    if (response.status === 200 || response.status === 202) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const GetAccountRagSyncJob = async (accountId, jobId) => {
  try {
    const response = await api.get(`/accounts/${accountId}/agentic/rag-sync/jobs/${jobId}`, buildAgenticRequestConfig());
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const GetLatestAccountRagSyncJob = async (accountId) => {
  try {
    const response = await api.get(`/accounts/${accountId}/agentic/rag-sync/jobs/latest`, buildAgenticRequestConfig());
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const ListAccountRagSyncAssetsStatus = async (accountId, params = {}) => {
  try {
    const response = await api.get(`/accounts/${accountId}/agentic/rag-sync/assets-status`, {
      ...buildAgenticRequestConfig(),
      params: {
        ...(params?.page ? { page: params.page } : {}),
        ...(params?.limit ? { limit: params.limit } : {}),
        ...(params?.hasMetadata ? { hasMetadata: true } : {})
      }
    });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const EnsureAgenticKnowledgeCollection = async (accountId, payload) => {
  try {
    const response = await api.post(`/accounts/${accountId}/agentic/knowledge/collections`, payload, buildAgenticRequestConfig());
    if (response.status === 200 || response.status === 201) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const UploadAgenticKnowledgeFile = async (accountId, formData) => {
  try {
    const response = await api.post(`/accounts/${accountId}/agentic/knowledge/files`, formData, buildAgenticRequestConfig());
    if (response.status === 200 || response.status === 201) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const ListAgenticKnowledgeFiles = async (accountId, ragKey) => {
  try {
    const response = await api.get(`/accounts/${accountId}/agentic/knowledge/files`, {
      ...buildAgenticRequestConfig(),
      params: { ragKey },
    });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const DeactivateAgenticKnowledgeFile = async (accountId, fileId, ragKey) => {
  try {
    const response = await api.delete(`/accounts/${accountId}/agentic/knowledge/files/${fileId}`, {
      ...buildAgenticRequestConfig(),
      params: { ragKey },
    });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const TriggerAgenticKnowledgeIngestion = async (accountId, payload) => {
  try {
    const response = await api.post(`/accounts/${accountId}/agentic/knowledge/ingest`, payload, buildAgenticRequestConfig());
    if (response.status === 200 || response.status === 202) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const GetAgenticKnowledgeIngestionJob = async (accountId, jobId) => {
  try {
    const response = await api.get(`/accounts/${accountId}/agentic/knowledge/ingest-jobs/${jobId}`, buildAgenticRequestConfig());
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const ListAgenticKnowledgeIngestionJobs = async (accountId, ragKey = "", filters = {}) => {
  try {
    const params = {
      ...(filters?.from ? { from: filters.from } : {}),
      ...(filters?.to ? { to: filters.to } : {}),
    };
    if (String(ragKey || "").trim()) {
      params.ragKey = String(ragKey).trim();
    }
    const response = await api.get(`/accounts/${accountId}/agentic/knowledge/ingest-jobs`, {
      ...buildAgenticRequestConfig(),
      params,
    });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const GetAgenticKnowledgeReadiness = async (accountId) => {
  try {
    const response = await api.get(`/accounts/${accountId}/agentic/knowledge/readiness`, buildAgenticRequestConfig());
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    const payload = error?.response?.data;
    if (payload?.overall === "ready" || payload?.overall === "degraded") {
      return payload;
    }
    return normalizeAgenticError(error);
  }
};

export const PullAgenticTrustvidKnowledge = async (accountId) => {
  try {
    const response = await api.post(
      `/accounts/${accountId}/agentic/knowledge/system/pull-trustvid-knowledge`,
      {},
      buildAgenticRequestConfig(),
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const IngestAgenticKnowledgeCorpus = async (accountId, payload = {}) => {
  try {
    const response = await api.post(
      `/accounts/${accountId}/agentic/knowledge/system/ingest-corpus`,
      payload,
      buildAgenticRequestConfig(),
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

/** Global Template Library / Designer Agent corpora (LanceDB) — same as npm run ingest:next. */
export const IngestAgenticNextPlatform = async (accountId, payload = {}) => {
  try {
    const response = await api.post(
      `/accounts/${accountId}/agentic/knowledge/system/ingest-next`,
      payload,
      buildAgenticRequestConfig(),
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const GetAgenticNextPlatformIngestStatus = async (accountId) => {
  try {
    const response = await api.get(
      `/accounts/${accountId}/agentic/knowledge/system/ingest-next/status`,
      buildAgenticRequestConfig(),
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const ClearAgenticNextPlatformIngest = async (accountId) => {
  try {
    const response = await api.post(
      `/accounts/${accountId}/agentic/knowledge/system/ingest-next/clear`,
      {},
      buildAgenticRequestConfig(),
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const GetAgenticNextPlatformHealth = async (accountId) => {
  try {
    const response = await api.get(
      `/accounts/${accountId}/agentic/knowledge/system/next-health`,
      {
        ...buildAgenticRequestConfig(),
        // API may forward degraded next health as 503 with a usable body.
        validateStatus: (status) => status === 200 || status === 503,
      },
    );
    if (response.status === 200 || response.status === 503) {
      const data = response.data || {};
      if (data?.status === "ok" || data?.status === "degraded" || Array.isArray(data?.domains)) {
        return data;
      }
    }
  } catch (error) {
    const payload = error?.response?.data;
    if (payload?.status === "ok" || payload?.status === "degraded") {
      return payload;
    }
    return normalizeAgenticError(error);
  }
};

export const ListAgenticNextPlatformDocuments = async (accountId, params = {}) => {
  try {
    const response = await api.get(
      `/accounts/${accountId}/agentic/knowledge/system/next-documents`,
      {
        ...buildAgenticRequestConfig(),
        params: {
          ...(params?.domain ? { domain: params.domain } : {}),
          ...(params?.limit != null ? { limit: params.limit } : {}),
          ...(params?.offset != null ? { offset: params.offset } : {}),
        },
      },
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const ListAgenticNextPlatformCorpora = async (accountId, params = {}) => {
  try {
    const response = await api.get(
      `/accounts/${accountId}/agentic/knowledge/system/next-corpora`,
      {
        ...buildAgenticRequestConfig(),
        params: {
          ...(params?.domain ? { domain: params.domain } : {}),
          ...(params?.path ? { path: params.path } : {}),
        },
      },
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const ListAgenticTokenUsage = async (accountId, limit = 50, filters = {}) => {
  try {
    const response = await api.get(`/accounts/${accountId}/agentic/knowledge/token-usage`, {
      ...buildAgenticRequestConfig(),
      params: { limit, ...(filters?.from ? { from: filters.from } : {}), ...(filters?.to ? { to: filters.to } : {}) },
    });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};

export const GetAgenticUsdInrRate = async (accountId) => {
  try {
    const response = await api.get(`/accounts/${accountId}/agentic/knowledge/usd-inr-rate`, {
      ...buildAgenticRequestConfig(),
    });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return normalizeAgenticError(error);
  }
};
